import type { ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import type { NavModule } from '@/layout/nav';
import { Badge } from '@/ui/Badge';
import { Card, CardBody, CardHeader } from '@/ui/Card';

/**
 * Lo que se ve si alguien entra a mano a la URL de un módulo que todavía no
 * existe. Dice **qué issue lo construye**, que es la única información útil
 * en ese momento.
 *
 * Cuando el módulo se implemente, esta ruta se reemplaza por el componente
 * real y la entrada de `nav.ts` pasa a `status: 'ready'`.
 */
export function ModulePlaceholder({ module }: { module: NavModule }): ReactNode {
  const { t } = useI18n();

  return (
    <Card className="mx-auto max-w-2xl">
      <CardHeader
        title={t(module.labelKey)}
        description={t(module.descriptionKey)}
        actions={<Badge tone="suspect">{t('nav.pending')}</Badge>}
      />
      <CardBody className="space-y-3">
        <h2 className="text-sm font-semibold text-ink">{t('placeholder.title')}</h2>
        {module.issue !== null && (
          <p className="text-sm text-ink-muted">
            {t('placeholder.detail', { issue: module.issue })}
          </p>
        )}
      </CardBody>
    </Card>
  );
}
